import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, LineChart, Line,
  XAxis, YAxis, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { Reveal } from "@/components/site/Reveal";

const BLUE = "#5B8CFF";
const GRID = "rgba(255,255,255,0.08)";

const useCountUp = (target, dur = 1300) => {
  const [v, setV] = useState(0);
  useEffect(() => {
    let start, raf;
    const step = (t) => {
      if (!start) start = t;
      const p = Math.min((t - start) / dur, 1);
      setV(target * (1 - Math.pow(1 - p, 3)));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, dur]);
  return v;
};

// Mirrors the real tenant dashboard's own stat-card style (see the live
// screenshot in the Hero) rather than a generic analytics-widget look.
const Stat = ({ label, value, fmt = (n) => Math.round(n), suffix = "", delta, live }) => {
  const v = useCountUp(value);
  return (
    <div className="bg-white/[0.03] border border-white/10 p-5 relative">
      {live && (
        <span className="absolute top-4 right-4 flex items-center gap-1.5 font-mono-t text-[9px] uppercase tracking-widest text-[#5B8CFF]">
          <span className="w-1.5 h-1.5 rounded-full bg-[#5B8CFF] animate-pulse" /> live
        </span>
      )}
      <div className="flex items-baseline gap-1">
        <span className="font-heading text-3xl md:text-4xl tracking-tight text-white">{fmt(v)}{suffix}</span>
        {delta && <span className="font-mono-t text-[11px] text-[#5B8CFF]">{delta}</span>}
      </div>
      <p className="mt-1.5 font-mono-t text-[10px] uppercase tracking-widest text-neutral-500">{label}</p>
    </div>
  );
};

const Panel = ({ title, children, className = "", right }) => (
  <div className={`bg-white/[0.03] border border-white/10 p-5 ${className}`}>
    <div className="flex items-center justify-between mb-4">
      <p className="font-mono-t text-[10px] uppercase tracking-widest text-neutral-500">{title}</p>
      {right}
    </div>
    {children}
  </div>
);

const Donut = ({ data }) => (
  <div className="flex items-center gap-4">
    <ResponsiveContainer width={110} height={110}>
      <PieChart>
        <Pie data={data} dataKey="value" innerRadius={34} outerRadius={52} paddingAngle={3} stroke="none" animationDuration={900}>
          {data.map((d, i) => <Cell key={i} fill={d.color} />)}
        </Pie>
      </PieChart>
    </ResponsiveContainer>
    <div className="space-y-2">
      {data.map((d) => (
        <div key={d.name} className="flex items-center gap-2 text-sm">
          <span className="w-2.5 h-2.5" style={{ background: d.color }} />
          <span className="text-neutral-300">{d.name}</span>
          <span className="font-mono-t text-neutral-500 text-xs ml-auto">{d.value}%</span>
        </div>
      ))}
    </div>
  </div>
);

const HBars = ({ data }) => (
  <div className="space-y-3">
    {data.map((d, i) => (
      <div key={d.name}>
        <div className="flex justify-between text-xs mb-1">
          <span className="text-neutral-300">{d.name}</span>
          <span className="font-mono-t text-neutral-500">{d.value}%</span>
        </div>
        <div className="h-2 bg-white/10">
          <motion.div
            className="h-full bg-[#5B8CFF]"
            initial={{ width: 0 }}
            whileInView={{ width: `${d.value}%` }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
          />
        </div>
      </div>
    ))}
  </div>
);

// Live-status rows exactly like the real dashboard's "Team status" panel —
// who's on a call vs available, with a Wallboard link out.
const TeamStatus = ({ agents, wallboardLabel = "Wallboard" }) => (
  <Panel
    title={`Team status · ${agents.filter((a) => a.status === "On call").length} on call · ${agents.filter((a) => a.status === "Available").length} available`}
    right={<span className="font-mono-t text-[10px] uppercase tracking-widest text-[#5B8CFF]">{wallboardLabel} →</span>}
  >
    <div className="space-y-3">
      {agents.map((a) => (
        <div key={a.name} className="flex items-center gap-3 text-sm">
          <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${a.status === "On call" ? "bg-[#5B8CFF] animate-pulse" : a.status === "Available" ? "bg-emerald-400" : "bg-neutral-600"}`} />
          <span className="text-neutral-300 flex-1">{a.name}</span>
          <span className="font-mono-t text-[11px] text-neutral-500">{a.status}</span>
        </div>
      ))}
    </div>
  </Panel>
);

const axisProps = { tick: { fill: "#737373", fontSize: 10, fontFamily: "JetBrains Mono" }, axisLine: false, tickLine: false };

/* ---------------- AI AGENT ANALYTICS ---------------- */
const aiCalls = [
  { d: "Mon", v: 82 }, { d: "Tue", v: 96 }, { d: "Wed", v: 74 },
  { d: "Thu", v: 120 }, { d: "Fri", v: 138 }, { d: "Sat", v: 64 }, { d: "Sun", v: 48 },
];
const aiSentiment = [
  { name: "Positive", value: 62, color: BLUE },
  { name: "Neutral", value: 28, color: "#9ca3af" },
  { name: "Negative", value: 10, color: "#525252" },
];
const aiIntent = [
  { name: "Sales", value: 44 }, { name: "Support", value: 33 },
  { name: "Billing", value: 15 }, { name: "Other", value: 8 },
];
const aiTopics = [
  { name: "Pricing & plans", value: 38 }, { name: "Trial setup", value: 27 },
  { name: "Integrations", value: 19 }, { name: "Multilingual", value: 16 },
];
const aiUnanswered = [
  { q: "Do you support Salesforce integration?", n: 12 },
  { q: "Can I use my own SIP trunk with the AI agent?", n: 8 },
  { q: "Is there a free trial for AI Business?", n: 5 },
];

export const AiAnalytics = () => (
  <div className="space-y-4" data-testid="analytics-ai">
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      <Reveal><Stat label="Calls handled today" value={622} delta="+14%" live /></Reveal>
      <Reveal delay={0.05}><Stat label="Avg sentiment" value={4.6} fmt={(n) => n.toFixed(1)} suffix="/5" delta="+0.3" /></Reveal>
      <Reveal delay={0.1}><Stat label="Resolution rate" value={91} suffix="%" delta="+6%" /></Reveal>
      <Reveal delay={0.15}><Stat label="Avg handle time" value={2.4} fmt={(n) => n.toFixed(1)} suffix="m" /></Reveal>
    </div>
    <div className="grid lg:grid-cols-3 gap-4">
      <Reveal className="lg:col-span-2">
        <Panel title="Calls per day">
          <ResponsiveContainer width="100%" height={190}>
            <AreaChart data={aiCalls} margin={{ top: 6, right: 6, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="aiFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={BLUE} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={BLUE} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid vertical={false} stroke={GRID} />
              <XAxis dataKey="d" {...axisProps} />
              <YAxis {...axisProps} />
              <Area type="monotone" dataKey="v" stroke={BLUE} strokeWidth={2} fill="url(#aiFill)" animationDuration={1100} dot={{ r: 3, fill: BLUE }} />
            </AreaChart>
          </ResponsiveContainer>
        </Panel>
      </Reveal>
      <Reveal delay={0.1}><Panel title="Sentiment"><div className="pt-2"><Donut data={aiSentiment} /></div></Panel></Reveal>
    </div>
    <div className="grid md:grid-cols-2 gap-4">
      <Reveal><Panel title="Intent detected"><HBars data={aiIntent} /></Panel></Reveal>
      <Reveal delay={0.1}><Panel title="Top topics"><HBars data={aiTopics} /></Panel></Reveal>
    </div>
    <Reveal>
      <Panel title="Unanswered-questions log" right={<span className="font-mono-t text-[10px] uppercase tracking-widest text-neutral-500">This week</span>}>
        <div className="space-y-2.5">
          {aiUnanswered.map((u) => (
            <div key={u.q} className="flex items-start justify-between gap-4 text-sm">
              <span className="text-neutral-300">{u.q}</span>
              <span className="font-mono-t text-neutral-500 shrink-0">{u.n}× asked</span>
            </div>
          ))}
        </div>
      </Panel>
    </Reveal>
  </div>
);

/* ---------------- PBX REPORTING ---------------- */
const pbxVolume = [
  { h: "9a", v: 40 }, { h: "11a", v: 72 }, { h: "1p", v: 58 },
  { h: "3p", v: 95 }, { h: "5p", v: 110 }, { h: "7p", v: 63 },
];
const pbxAnswered = [
  { name: "Answered", value: 88, color: BLUE },
  { name: "Missed", value: 12, color: "#525252" },
];
const pbxWait = [
  { d: "Mon", v: 34 }, { d: "Tue", v: 41 }, { d: "Wed", v: 28 },
  { d: "Thu", v: 52 }, { d: "Fri", v: 46 }, { d: "Sat", v: 22 }, { d: "Sun", v: 18 },
];
const pbxUsage = [
  { name: "Sales queue", value: 72 }, { name: "Support queue", value: 58 },
  { name: "SIP trunk A", value: 41 }, { name: "SIP trunk B", value: 24 },
];
const pbxTeam = [
  { name: "Priya S. · ext 101", status: "On call" },
  { name: "Rahul M. · ext 102", status: "Available" },
  { name: "Ananya K. · ext 103", status: "Available" },
  { name: "Vikram T. · ext 104", status: "Away" },
];

export const PbxAnalytics = () => (
  <div className="space-y-4" data-testid="analytics-pbx">
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      <Reveal><Stat label="Total calls today" value={1284} delta="+9%" live /></Reveal>
      <Reveal delay={0.05}><Stat label="Answered rate" value={88} suffix="%" delta="+3%" /></Reveal>
      <Reveal delay={0.1}><Stat label="Avg queue wait" value={38} suffix="s" delta="-11%" /></Reveal>
      <Reveal delay={0.15}><Stat label="Active trunks" value={6} /></Reveal>
    </div>
    <div className="grid lg:grid-cols-3 gap-4">
      <Reveal className="lg:col-span-2">
        <Panel title="Call volume by hour">
          <ResponsiveContainer width="100%" height={190}>
            <BarChart data={pbxVolume} margin={{ top: 6, right: 6, left: -20, bottom: 0 }}>
              <CartesianGrid vertical={false} stroke={GRID} />
              <XAxis dataKey="h" {...axisProps} />
              <YAxis {...axisProps} />
              <Bar dataKey="v" fill={BLUE} radius={[2, 2, 0, 0]} animationDuration={1000} />
            </BarChart>
          </ResponsiveContainer>
        </Panel>
      </Reveal>
      <Reveal delay={0.1}><Panel title="Answered vs missed"><div className="pt-2"><Donut data={pbxAnswered} /></div></Panel></Reveal>
    </div>
    <div className="grid lg:grid-cols-3 gap-4">
      <Reveal className="lg:col-span-2">
        <Panel title="Avg queue wait (s)">
          <ResponsiveContainer width="100%" height={170}>
            <LineChart data={pbxWait} margin={{ top: 6, right: 6, left: -20, bottom: 0 }}>
              <CartesianGrid vertical={false} stroke={GRID} />
              <XAxis dataKey="d" {...axisProps} />
              <YAxis {...axisProps} />
              <Line type="monotone" dataKey="v" stroke={BLUE} strokeWidth={2} dot={{ r: 3, fill: BLUE }} animationDuration={1100} />
            </LineChart>
          </ResponsiveContainer>
        </Panel>
      </Reveal>
      <Reveal delay={0.1}><Panel title="Queue & trunk usage"><HBars data={pbxUsage} /></Panel></Reveal>
    </div>
    <Reveal><TeamStatus agents={pbxTeam} /></Reveal>
  </div>
);

export const ReportingDashboard = ({ kind }) => (kind === "pbx" ? <PbxAnalytics /> : <AiAnalytics />);
